import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Select, Typography, Row, Col, Spin, Collapse, Flex, InputNumber, Slider, Alert, Tooltip, Card, Divider, Badge, Space, Radio, message } from "antd";
import { InfoCircleOutlined, MailOutlined, CalendarOutlined, PercentageOutlined, BellOutlined, UserOutlined } from "@ant-design/icons";

import { updateOrganization } from "../../redux/organizationSlice";
import SaveButton from "../UI/savebutton";

const { Title } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const NotificationAccounts = () => {
  const dispatch = useDispatch();
  const { organizationData, loading } = useSelector(
    (state) => state.organization
  );

  const [jobBookingNotifications, setJobBookingNotifications] = useState([]);
  const [leadNotifications, setLeadNotifications] = useState([]);
  const [abandonmentRateThreshold, setAbandonmentRateThreshold] = useState(50);
  const [abandonmentRateDayRange, setAbandonmentRateDayRange] = useState(7);
  const [abandonmentRateEmails, setAbandonmentRateEmails] = useState([]);
  const [notificationCooldownHours, setNotificationCooldownHours] = useState(24);
  const [customerCountingType, setCustomerCountingType] = useState("unique");
  const [isDirty, setIsDirty] = useState(false);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    if (isUpdatingRef.current) {
      isUpdatingRef.current = false;
      setIsDirty(false);
      return;
    }

    if (!organizationData) return;

    const newJobBookingNotifications = organizationData?.notificationAccounts || [];
    const newLeadNotifications = organizationData?.leadNotifications || [];
    const abandonmentConfig = organizationData?.abandonmentRateNotification || {};
    const newThreshold = abandonmentConfig?.threshold ?? 50;
    const newDayRange = abandonmentConfig?.dayRange ?? 7;
    const newEmails = abandonmentConfig?.emails || [];
    const newCooldownHours = abandonmentConfig?.notificationCooldownHours ?? 24;
    const newCountingType = abandonmentConfig?.countType ?? "unique";

    setJobBookingNotifications(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(newJobBookingNotifications)) {
        return newJobBookingNotifications;
      }
      return prev;
    });
    
    setLeadNotifications(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(newLeadNotifications)) {
        return newLeadNotifications;
      }
      return prev;
    });
    
    setAbandonmentRateThreshold(prev => {
      if (prev !== newThreshold) {
        return newThreshold;
      }
      return prev;
    });
    
    setAbandonmentRateDayRange(prev => {
      if (prev !== newDayRange) {
        return newDayRange;
      }
      return prev;
    });
    
    setAbandonmentRateEmails(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(newEmails)) {
        return newEmails;
      }
      return prev;
    });
    
    setNotificationCooldownHours(prev => {
      if (prev !== newCooldownHours) {
        return newCooldownHours;
      }
      return prev;
    });
    
    setCustomerCountingType(prev => {
      if (prev !== newCountingType) {
        return newCountingType;
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData?._id, organizationData?.notificationAccounts, organizationData?.leadNotifications, organizationData?.abandonmentRateNotification]);
  useEffect(() => {
    if (!organizationData) return;
    const abandonmentConfig = organizationData?.abandonmentRateNotification || {};
    const originalJobBookingNotifications = organizationData?.notificationAccounts || [];
    const originalLeadNotifications = organizationData?.leadNotifications || [];
    const originalThreshold = abandonmentConfig.threshold ?? 50;
    const originalDayRange = abandonmentConfig.dayRange ?? 7;
    const originalEmails = abandonmentConfig.emails || [];
    const originalCooldownHours = abandonmentConfig.notificationCooldownHours ?? 24;
    const originalCountingType = abandonmentConfig.countType ?? "unique";

    const hasChanges = 
      JSON.stringify(jobBookingNotifications) !== JSON.stringify(originalJobBookingNotifications) ||
      JSON.stringify(leadNotifications) !== JSON.stringify(originalLeadNotifications) ||
      abandonmentRateThreshold !== originalThreshold ||
      abandonmentRateDayRange !== originalDayRange ||
      JSON.stringify(abandonmentRateEmails) !== JSON.stringify(originalEmails) ||
      notificationCooldownHours !== originalCooldownHours ||
      customerCountingType !== originalCountingType;

    setIsDirty(hasChanges);
  }, [jobBookingNotifications, leadNotifications, abandonmentRateThreshold, abandonmentRateDayRange, abandonmentRateEmails, notificationCooldownHours, customerCountingType, organizationData]);

  const hasDuplicate = (emails, newEmail) => {
    return emails.includes(newEmail.trim().toLowerCase());
  };

  const normalizedJobBooking = useMemo(() => {
    return jobBookingNotifications
      .map(e => e && e.trim().toLowerCase())
      .filter(Boolean);
  }, [jobBookingNotifications]);
  
  const normalizedLeadNotifications = useMemo(() => {
    return leadNotifications
      .map(e => e && e.trim().toLowerCase())
      .filter(Boolean);
  }, [leadNotifications]);
  
  const normalizedAbandonmentEmails = useMemo(() => {
    return abandonmentRateEmails
      .map(e => e && e.trim().toLowerCase())
      .filter(Boolean);
  }, [abandonmentRateEmails]);

  const handleJobBookingChange = (value) => {
    if (value.length > normalizedJobBooking?.length){
      const lastEmail = value[value.length - 1];
      if (lastEmail && hasDuplicate(normalizedJobBooking, lastEmail)) {
        message.warning(`"${lastEmail}" is already in the list`);
        return;
      }
    }
    setJobBookingNotifications(value);
  };

  const handleLeadNotificationsChange = (value) => {
    if (value.length> normalizedLeadNotifications?.length){
    const lastEmail = value[value.length - 1];
    if (lastEmail && hasDuplicate(normalizedLeadNotifications, lastEmail)) {
      message.warning(`"${lastEmail}" is already in the list`);
      return;
    }
  }
    setLeadNotifications(value);
  };

  const handleAbandonmentRateThresholdChange = (value) => {
    if (value === null || value === undefined) return;
    setAbandonmentRateThreshold(value);
  };

  const handleAbandonmentRateDayRangeChange = (value) => {
    if (value === null || value === undefined || value < 1 || value > 30) return;
    setAbandonmentRateDayRange(value);
  };

  const handleAbandonmentRateEmailsChange = (value) => {
    if (value.length> normalizedAbandonmentEmails?.length){
    const lastEmail = value[value.length - 1];
    if (lastEmail && hasDuplicate(normalizedAbandonmentEmails, lastEmail)) {
      message.warning(`"${lastEmail}" is already in the list`);
      return; 
    }
  }
    
    setAbandonmentRateEmails(value);
  };

  const handleNotificationCooldownHoursChange = (value) => {
    if (value === null || value === undefined) return;
    setNotificationCooldownHours(value);
  };

  const handleCustomerCountingTypeChange = (e) => {
    setCustomerCountingType(e.target.value);
  };

  const handleSave = () => {
    if (organizationData?._id) {
      isUpdatingRef.current = true;
      dispatch(
        updateOrganization({
          id: organizationData._id,
          payload: {
            notificationAccounts: jobBookingNotifications,
            leadNotifications: leadNotifications,
            abandonmentRateNotification: {
              threshold: abandonmentRateThreshold,
              dayRange: abandonmentRateDayRange,
              emails: abandonmentRateEmails,
              notificationCooldownHours: notificationCooldownHours,
              countType: customerCountingType,
            },
          },
          page: "Notification Configurations"
        })
      );
    }
  };

  return (
    <>
         <style>
        {`
          .abandonment-slider-wrapper .ant-slider-dot {
            display: none !important;
          }
          .abandonment-slider-wrapper .ant-slider-dot-active {
            display: none !important;
          }
          .abandonment-slider-wrapper .ant-slider-mark {
            margin-top: 8px;
          }
          .abandonment-slider-wrapper .ant-slider-mark-text {
            color: #6b7280 !important;
            font-size: 11px !important;
          }
          .abandonment-slider-wrapper .ant-slider-mark > span {
            background: transparent !important;
            border: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          .abandonment-rate-card {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            background: #fafafa;
            transition: all 0.3s ease;
          }
          .abandonment-rate-card:hover {
            border-color: #d1d5db;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          }
          .setting-section {
            background: white;
            border-radius: 6px;
            padding: 16px;
            margin-bottom: 16px;
            border: 1px solid #e5e7eb;
          }
        `}
      </style>
      <Spin spinning={loading} fullscreen />
      <Flex align="center" gap={10} style={{ marginBottom: "20px" }}>
        <img
          src={require("../../assets/Images/notification.png")}
          height={20}
          width={20}
          alt="map-logo"
          style={{
            verticalAlign: "middle",
            display: "inline-block",
          }}
        />
        <Title level={3} style={{ margin: 0 }}>
          Notification Configurations
        </Title>
      </Flex>
      <Row>
        <Col span={24}>
          <Collapse
            defaultActiveKey={["1", "2", "3"]}
            style={{ marginBottom: "20px" }}
          >
            <Panel
              style={{ fontWeight: 500 }}
              header="Job Booking Notification"
              key="1"
            >
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Add job booking notifications"
                value={jobBookingNotifications}
                onChange={handleJobBookingChange}
                tokenSeparators={[","]}
                open={false}
                suffixIcon={null}
              >
                {jobBookingNotifications.map((notification) => (
                  <Option key={notification} value={notification}>
                    {notification}
                  </Option>
                ))}
              </Select>
            </Panel>

            <Panel
              style={{ fontWeight: 500 }}
              header="Lead Notifications"
              key="2"
            >
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Add lead notifications"
                value={leadNotifications}
                onChange={handleLeadNotificationsChange}
                tokenSeparators={[","]}
                open={false}
                suffixIcon={null}
              >
                {leadNotifications.map((notification) => (
                  <Option key={notification} value={notification}>
                    {notification}
                  </Option>
                ))}
              </Select>
            </Panel>

            <Panel
              style={{ fontWeight: 500 }}
              header={
                <Space>
                  <span>Abandonment Rate Notification</span>
                </Space>
              }
              key="3"
            >
              <Card 
                className="abandonment-rate-card"
                style={{ marginBottom: "20px" }}
                bodyStyle={{ padding: "20px" }}
              >
                  
                <div className="setting-section">
                  <Space align="center" style={{ marginBottom: "16px", width: "100%" }}>
                    <PercentageOutlined style={{ fontSize: "18px", color: "#ef4444" }} />
                    <label
                      style={{
                        fontWeight: "600",
                        color: "#1f2937",
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                      }}
                    >
                      Abandonment Rate Threshold
                      <Tooltip title="The abandonment rate percentage that will trigger a notification (0-100%)">
                        <InfoCircleOutlined style={{ fontSize: "14px", color: "#6b7280", cursor: "help" }} />
                      </Tooltip>
                      <span style={{ 
                        color: "#6b7280",
                        fontSize: "13px",
                        fontWeight: "500",
                        marginLeft: "8px"
                      }}>
                        ({abandonmentRateThreshold}%)
                      </span>
                    </label>
                  </Space>

                  <div className="abandonment-slider-wrapper" style={{ padding: "0 8px" }}>
                    <Slider
                      min={0}
                      max={100}
                      dots={false}
                      styles={{
                        track: {
                          backgroundColor: "#ef4444",
                        },
                        rail: {
                          backgroundColor: "#fee2e2",
                        },
                      }}
                      value={abandonmentRateThreshold}
                      onChange={handleAbandonmentRateThresholdChange}
                      marks={{
                        0: { label: "0%", style: { color: "#6b7280", fontSize: "11px" } },
                        25: { label: "25%", style: { color: "#6b7280", fontSize: "11px" } },
                        50: { label: "50%", style: { color: "#6b7280", fontSize: "11px" } },
                        75: { label: "75%", style: { color: "#6b7280", fontSize: "11px" } },
                        100: { label: "100%", style: { color: "#6b7280", fontSize: "11px" } },
                      }}
                    />
                  </div>
                </div>

                <Divider style={{ margin: "20px 0" }} />

                <div className="setting-section">
                  <Space align="center" style={{ marginBottom: "16px", width: "100%" }}>
                    <CalendarOutlined style={{ fontSize: "18px", color: "#3b82f6" }} />
                    <label
                      style={{
                        fontWeight: "600",
                        color: "#1f2937",
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                      }}
                    >
                      Day Range
                      <Tooltip title="The number of days to consider when calculating the abandonment rate (1-30 days)">
                        <InfoCircleOutlined style={{ fontSize: "14px", color: "#6b7280", cursor: "help" }} />
                      </Tooltip>
                    </label>
                  </Space>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <InputNumber
                      min={1}
                      max={30}
                      value={abandonmentRateDayRange}
                      onChange={handleAbandonmentRateDayRangeChange}
                      size="large"
                      style={{ width: "140px" }}
                      addonAfter={<span style={{ color: "#6b7280" }}>days</span>}
                    />
                  </div>
                </div>

                <Divider style={{ margin: "20px 0" }} />

                <div className="setting-section">
                  <Space align="center" style={{ marginBottom: "16px", width: "100%" }}>
                    <UserOutlined style={{ fontSize: "18px", color: "#8b5cf6" }} />
                    <label
                      style={{
                        fontWeight: "600",
                        color: "#1f2937",
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                      }}
                    >
                      Customer Counting Method
                      <Tooltip title="Choose whether to count customers as unique (each customer counted once; if they have any completed journey in the time range, they won't be counted as abandoned) or total (all customer interactions counted separately)">
                        <InfoCircleOutlined style={{ fontSize: "14px", color: "#6b7280", cursor: "help" }} />
                      </Tooltip>
                    </label>
                  </Space>
                  
                  <Radio.Group
                    value={customerCountingType}
                    onChange={handleCustomerCountingTypeChange}
                    size="large"
                    style={{ width: "100%" }}
                  >
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                      <Radio value="unique" style={{ display: "flex", alignItems: "flex-start", padding: "12px", borderRadius: "6px", border: "1px solid #e5e7eb", width: "100%" }}>
                        <div style={{ marginLeft: "8px" }}>
                          <div style={{ fontWeight: "600", color: "#1f2937", marginBottom: "4px" }}>Unique</div>
                          <div style={{ fontSize: "13px", color: "#6b7280" }}>Each customer counted once. If a customer has any completed journey within the time range, they are not counted as abandoned, even if they also have abandoned journeys.</div>
                        </div>
                      </Radio>
                      <Radio value="total" style={{ display: "flex", alignItems: "flex-start", padding: "12px", borderRadius: "6px", border: "1px solid #e5e7eb", width: "100%" }}>
                        <div style={{ marginLeft: "8px" }}>
                          <div style={{ fontWeight: "600", color: "#1f2937", marginBottom: "4px" }}>Total</div>
                          <div style={{ fontSize: "13px", color: "#6b7280" }}>All customer interactions counted separately</div>
                        </div>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </div>

                <Divider style={{ margin: "20px 0" }} />

                <div className="setting-section">
                  <Space align="center" style={{ marginBottom: "16px", width: "100%" }}>
                    <BellOutlined style={{ fontSize: "18px", color: "#f59e0b" }} />
                    <label
                      style={{
                        fontWeight: "600",
                        color: "#1f2937",
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                      }}
                    >
                      Notification Cooldown Period
                      <Tooltip title="The minimum number of hours that must pass before sending another abandonment rate notification (prevents spam)">
                        <InfoCircleOutlined style={{ fontSize: "14px", color: "#6b7280", cursor: "help" }} />
                      </Tooltip>
                    </label>
                  </Space>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <InputNumber
                      min={1}
                      max={168}
                      value={notificationCooldownHours}
                      onChange={handleNotificationCooldownHoursChange}
                      size="large"
                      style={{ width: "140px" }}
                      addonAfter={<span style={{ color: "#6b7280" }}>hours</span>}
                    />
                  </div>
                </div>

                <Divider style={{ margin: "20px 0" }} />


                <div className="setting-section" style={{ marginBottom: "0" }}>
                  <Space align="center" style={{ marginBottom: "16px", width: "100%" }}>
                    <MailOutlined style={{ fontSize: "18px", color: "#10b981" }} />
                    <label
                      style={{
                        fontWeight: "600",
                        color: "#1f2937",
                        fontSize: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                      }}
                    >
                      Recipient Email Addresses
                      {abandonmentRateEmails.length > 0 && (
                        <Badge count={abandonmentRateEmails.length} style={{ backgroundColor: "#10b981" }} />
                      )}
                    </label>
                  </Space>
                  
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Add email addresses (e.g., email@example.com)"
                    value={abandonmentRateEmails}
                    onChange={handleAbandonmentRateEmailsChange}
                    tokenSeparators={[","]}
                    open={false}
                    suffixIcon={null}
                    size="large"
                  >
                    {abandonmentRateEmails.map((email) => (
                      <Option key={email} value={email}>
                        {email}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Card>

              <Alert
                message="Notification Trigger"
                description={
                  <div style={{ marginTop: "4px", lineHeight: "1.6" }}>
                    When the abandonment rate exceeds{" "}
                    <strong style={{ color: "#dc2626" }}>{abandonmentRateThreshold}%</strong> over the past{" "}
                    <strong style={{ color: "#3b82f6" }}>{abandonmentRateDayRange} day{abandonmentRateDayRange !== 1 ? "s" : ""}</strong>, 
                    an email notification will be sent to{" "}
                    <strong style={{ color: "#10b981" }}>
                      {`${abandonmentRateEmails.length} recipient${abandonmentRateEmails.length > 1 ? "s" : ""}`}
                    </strong>. 
                    Customers will be counted as <strong style={{ color: "#8b5cf6" }}>{customerCountingType === "unique" ? "unique (completed journeys take priority over abandoned ones)" : "total"}</strong> for the calculation.
                    Notifications will only be sent if at least{" "}
                    <strong style={{ color: "#f59e0b" }}>{notificationCooldownHours} hour{notificationCooldownHours !== 1 ? "s" : ""}</strong> have passed since the last notification.
                  </div>
                }
                type="info"
                showIcon
                icon={<BellOutlined />}
                style={{
                  borderRadius: "8px",
                  marginBottom: "20px",
                  border: "1px solid #bfdbfe",
                  backgroundColor: "#eff6ff",
                }}
              />
            </Panel>
          </Collapse>
        </Col>
      </Row>
      <SaveButton onSubmit={handleSave} isDirty={isDirty} />
    </>
  );
};

export default NotificationAccounts;
